import { CSSProperties, useEffect } from 'react';
import { registerModalBackHandler } from '../../../lib/native';
import { Button } from '../core/Button';

/**
 * ConfirmDialog — centered alert over a dim backdrop: serif title, short body
 * and a stack of full-width actions (primary first). Used to stop an exit that
 * would lose work. `onCancel` fires from the backdrop, the Android back button
 * and the "Keep editing"-style action the caller passes in.
 */
export interface ConfirmDialogAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  disabled?: boolean;
}

export interface ConfirmDialogProps {
  title: string;
  body?: string;
  /** Rendered top to bottom — put the recommended action first. */
  actions: ConfirmDialogAction[];
  onCancel: () => void;
  style?: CSSProperties;
}

export function ConfirmDialog({ title, body, actions, onCancel, style }: ConfirmDialogProps) {
  useEffect(() => registerModalBackHandler(onCancel), [onCancel]);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 340, background: 'var(--surface)', border: '1px solid var(--line)',
          borderRadius: 18, padding: 22, fontFamily: 'var(--font-sans)', ...style,
        }}
      >
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'calc(21px * var(--text-scale))', lineHeight: 1.15, color: 'var(--text)', margin: 0 }}>{title}</h2>
        {body && <p style={{ fontSize: 'var(--fs-footnote)', lineHeight: 1.5, color: 'var(--text-soft)', margin: '8px 0 0' }}>{body}</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 18 }}>
          {actions.map((a) => (
            <Button key={a.label} variant={a.variant ?? 'secondary'} onClick={a.onClick} disabled={a.disabled} className="w-full min-h-[44px]">
              {a.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
